import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { ScriptLoaderService } from '../../services/script-loader.service';
import { LocaleService } from '../../services/locale/locale.service';
import { Address, CFR_CONFIGURATION, CfrConfig } from '../../domain/address.domain';
import { Binding, Options, FieldMode } from './pca.domain';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let pca: any;

/* eslint-disable @typescript-eslint/naming-convention */
export interface CanadaPostAddress {
  BuildingNumber: string
  SubBuilding: string
  City: string
  PostalCode: string
  CountryName: string
  CountryIso2: string
  ProvinceCode: string
  Line1: string
  POBoxNumber: string
  Street: string
}
/* eslint-enable @typescript-eslint/naming-convention */

@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private readonly cfrConfig = inject<CfrConfig>(CFR_CONFIGURATION, { optional: true });
  private readonly scriptLoader = inject(ScriptLoaderService);
  private readonly localeService = inject(LocaleService);

  // object required to set up address capture
  control: { listen: (action: string, callback: (address: CanadaPostAddress) => void) => void };
  // field binding for search field and autopopulate
  fields: Array<Binding> = [
    { element: 'td-wb-address-street-', field: '', mode: FieldMode.SEARCH },
    { element: 'td-wb-address-street-num-', field: '', mode: FieldMode.SEARCH },
    { element: 'td-wb-address-unit-', field: 'SubBuilding', mode: FieldMode.POPULATE },
    { element: 'td-wb-address-city-', field: 'City', mode: FieldMode.POPULATE },
    { element: 'td-wb-address-postalcode-', field: 'PostalCode', mode: FieldMode.POPULATE },
    { element: 'td-wb-address-territory-', field: 'ProvinceName', mode: FieldMode.POPULATE }
  ];

  // For MVP1, restricting addresses to Canada only
  // If supporting all countries, remove showCountry (defaults to true)
  // and add assets/styling from http://ws1.postescanada-canadapost.ca/css/addresscomplete-2.30.min.css as needed
  options: Options = {
    key: '',
    bar: {
      showCountry: false,
      showLogo: false,
      logoLink: false
    }
  };

  provinces: Map<string, string> = new Map<string, string>([['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'],
    ['NL', 'Newfoundland and Labrador'], ['NT', 'Northwest Territories'], ['NS', 'Nova Scotia'], ['NU', 'Nunavut'],
    ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'], ['SK', 'Saskatchewan'], ['YT', 'Yukon']]);

  RURAL_PO_TYPES: Array<string> = ['PO_BOX', 'RURAL_ROUTE'];
  readonly onSelectedAddressChange: BehaviorSubject<Address>;

  private selectedAddress: Address = {
    streetName: '',
    streetNum: '',
    unitNum: '',
    poBox: '',
    cityName: '',
    territory: '',
    postalCode: '',
    addressLine: [''],
    territoryCd: '',
    countryCd: '',
    postalStationTxt: ''
  };

  static readonly RURAL_ROUTE_TYPE: string = 'RURAL_ROUTE';
  static readonly PO_BOX_TYPE: string = 'PO_BOX';

  constructor() {
    this.onSelectedAddressChange = new BehaviorSubject<Address>(this.selectedAddress);
  }

  loadAddressComplete(usageId: string): void {
    this.fields.forEach((field: Binding) => {
      field.element += usageId;
    });
    if (this.cfrConfig !== undefined && this.cfrConfig !== null &&
            this.cfrConfig.addressCompleteURL !== undefined && this.cfrConfig.addressCompleteKey !== undefined) {
      const url: string = this.cfrConfig.addressCompleteURL + this.cfrConfig.addressCompleteKey;
      this.options.key = this.cfrConfig.addressCompleteKey;
      this.options.culture = this.localeService.getLocale();
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const base = this;

      // @ts-expect-error TODO still not sure why this param is checked but not used...
      this.scriptLoader.load(url).then((result: Array<unknown>): void => {
        if (result !== undefined && result.length > 0) {
          this.control = new pca.Address(this.fields, this.options);
          base.control.listen('populate', function(address: CanadaPostAddress) {
            base.selectedAddress.streetNum = address.BuildingNumber;
            // Do not autofill street field for PO boxes - still needed for RR (rural)
            base.selectedAddress.unitNum = address.SubBuilding;
            base.selectedAddress.poBox = address.POBoxNumber;
            base.selectedAddress.cityName = address.City;
            base.selectedAddress.postalCode = address.PostalCode.replace(/\s/g, '');
            base.selectedAddress.territory = address.ProvinceCode;
            base.selectedAddress.country = address.CountryName;
            base.selectedAddress.countryCd = address.CountryIso2;
            base.selectedAddress.territoryCd = address.ProvinceCode;
            if (address.Line1 !== undefined) {
              base.selectedAddress.addressLine = new Array<string>(address.Line1);
              const ruralRouteNum = base.parseRuralRoute(address.Line1);
              base.selectedAddress.postalStationTxt = '';
              base.selectedAddress.ruralRouteNum = '';
              base.selectedAddress.ruralRouteTypeCd = '';

              if (ruralRouteNum) {
                base.selectedAddress.ruralRouteNum = ruralRouteNum;
                base.selectedAddress.ruralRouteTypeCd = AddressService.RURAL_ROUTE_TYPE;
                base.selectedAddress.postalStationTxt = base.parseStationForRuralRoute(address.Line1);
              } else if (address.POBoxNumber) {
                base.selectedAddress.postalStationTxt = base.parseStationForPOBox(address.Line1, address.POBoxNumber);
              }
            }

            if (address.Street === '' && (address.POBoxNumber === '' && base.selectedAddress.ruralRouteNum === '')) {
              base.selectedAddress.streetName = address.Line1;
            } else {
              base.selectedAddress.streetName = address.Street;
            }
            base.onSelectedAddressChange.next(base.selectedAddress);
          });
        }
      });
    }
  }

  // extract street number suffix from street number ie from 5A, return A
  parseStreetNumberSuffix(streetNum: string): string {
    return streetNum?.replace(/[^A-Za-z]/g,'');
  }

  // extract street number from street number ie from 5A, return 5
  parseStreetNumber(streetNum: string): string {
    return streetNum?.replace(/[^0-9]/g,'');
  }

  parseRuralRoute(address: string): string {
    if (address.startsWith('RR ')) {
      return address.match(/(\d+)/)?.[0];
    }
    return null;
  }

  parseStationForRuralRoute(address: string): string {
    return address?.replace('RR ' + this.parseRuralRoute(address), '');
  }

  parseStationForPOBox(line1: string, poBoxNumber: string): string {
    return line1?.replace('PO Box ' + poBoxNumber, '')?.trim();
  }

  resetScript(): void {
    this.scriptLoader.removeScript(this.cfrConfig?.addressCompleteURL + this.cfrConfig?.addressCompleteKey);
  }
}
